import { ReactNode, Ref } from 'react';
import {
  forwardRef,
  FormControl,
  FormHelperText,
  FormErrorMessage,
  FormControlProps,
  Radio as ChakraRadio,
  RadioProps as ChakraRadioProps,
} from '@chakra-ui/react';

type RadioInputProps = ChakraRadioProps['inputProps'] & {
  'data-testid'?: string;
};

export interface RadioProps
  extends Omit<FormControlProps, 'icon' | 'iconColor' | 'iconSize' | 'inputProps' | 'value' | 'onChange'> {
  inputProps?: RadioInputProps;
  helperText?: ReactNode;
  errorText?: string;
  value?: ChakraRadioProps['value'];
  inputRef?: Ref<HTMLInputElement>;
  isChecked?: ChakraRadioProps['isChecked'];
  isDisabled?: ChakraRadioProps['isDisabled'];
  onChange?: ChakraRadioProps['onChange'];
}

const Radio = forwardRef<RadioProps, 'input'>((props, ref) => {
  const {
    'aria-label': ariaLabel,
    helperText,
    isInvalid,
    errorText,
    autoFocus,
    name,
    onBlur,
    role,
    value,
    inputRef,
    inputProps,
    children,
    isChecked,
    isDisabled,
    onChange,
    id,
    ...rest
  } = props;

  const formControlProps = {
    isInvalid: isInvalid || !!errorText,
    ref,
    ...rest,
  };

  const radioProps: ChakraRadioProps = {
    autoFocus,
    children,
    name,
    onBlur,
    role,
    value,
    isChecked,
    isDisabled,
    onChange,
    id,
  };

  radioProps.inputProps = { ...inputProps };

  if (ariaLabel && !inputProps?.['aria-label']) {
    radioProps.inputProps['aria-label'] = ariaLabel;
  }

  return (
    <FormControl {...formControlProps}>
      <ChakraRadio {...radioProps} ref={inputRef} />
      {errorText && (
        <FormErrorMessage marginLeft="32" marginTop="0" as="p">
          {errorText}
        </FormErrorMessage>
      )}
      {!errorText && helperText && (
        <FormHelperText marginLeft="32" marginTop="0" as="p">
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
});

export default Radio;
